import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { Modal } from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import Select from '../../ui/PortalSelect';

import { baseURL } from '../backend_url';
import {
  fetchJson,
  loadAccessibleCompanies,
  readSessionUser,
  requireArray,
} from '../accountAccess';
import {
  DataRegion,
  EmptyState,
  ErrorState,
  FormSection,
  LoadingSkeleton,
  PageHeader,
} from '../../ui';
import { getAuditChangeReason } from './auditPresentation';
import './EventLog.css';

const DEFAULT_FILTERS = {
  locationId: '',
  user: '',
  startDate: '',
  endDate: '',
  search: '',
};
const PAGE_SIZE = 50;

const formatTimestamp = (value) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const changedFieldCount = (event) => Object.keys(event.changed_data || {}).length;

export default function EventLog() {
  const [lgShow, setLgShow] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [modules, setModules] = useState([]);
  const [moduleKey, setModuleKey] = useState('');
  const [locations, setLocations] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [events, setEvents] = useState([]);
  const [setupError, setSetupError] = useState('');
  const [fetchError, setFetchError] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [page, setPage] = useState(0);
  const locationRequestRef = useRef(0);

  const selectedCompany = useMemo(
    () => companies.find((company) => String(company._id) === companyId),
    [companies, companyId]
  );
  const selectedModule = useMemo(
    () => modules.find((module) => module.key === moduleKey),
    [modules, moduleKey]
  );

  const accountOptions = useMemo(() => companies.map((company) => ({
    value: String(company._id),
    label: `${company.company_name} (${company._id})`,
  })), [companies]);

  const moduleOptions = useMemo(() => modules.map((module) => ({
    value: module.key,
    label: module.label,
  })), [modules]);

  const locationOptions = useMemo(() => locations.map((location) => ({
    value: location.location_id || location._id,
    label: `${location.location_name} (${location.location_id || location._id})`,
  })), [locations]);

  const invalidateResults = () => {
    setPage(0);
    setHasFetched(false);
    setEvents([]);
    setFetchError('');
  };

  useEffect(() => {
    const { user, error } = readSessionUser();
    if (error) {
      setSetupError(error);
      return;
    }

    let active = true;
    Promise.all([
      loadAccessibleCompanies(baseURL, user, 'EventLog_Account Access'),
      fetchJson(
        `${baseURL}/get_audit_modules?companyNumber=${encodeURIComponent(String(user.account_id))}`,
        {},
        'Audit module list'
      ).then((data) => requireArray(data, 'Audit module list')),
    ])
      .then(([availableCompanies, availableModules]) => {
        if (!active) return;
        setCompanies(availableCompanies);
        setModules(availableModules);
        setCompanyId(availableCompanies.length === 1 ? String(availableCompanies[0]._id) : '');
        setSetupError('');
      })
      .catch((loadError) => {
        if (!active) return;
        setCompanies([]);
        setModules([]);
        setSetupError(loadError.message || 'Audit filters could not be loaded.');
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setHasFetched(false);
    setEvents([]);
    setFetchError('');
    setLocations([]);
    setFilters(DEFAULT_FILTERS);
    if (!companyId) return;

    const requestId = locationRequestRef.current + 1;
    locationRequestRef.current = requestId;
    fetchJson(
      `${baseURL}/get_company_location_inactive_active/${encodeURIComponent(companyId)}`
        + `?companyNumber=${encodeURIComponent(companyId)}`,
      {},
      'Location list'
    )
      .then((data) => {
        if (locationRequestRef.current !== requestId) return;
        setLocations(requireArray(data?.res, 'Location list'));
      })
      .catch((loadError) => {
        if (locationRequestRef.current !== requestId) return;
        setLocations([]);
        setFetchError(loadError.message || 'Locations could not be loaded.');
      });
  }, [companyId]);

  const updateFilter = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
    invalidateResults();
  };

  const handleModuleChange = (option) => {
    setModuleKey(option?.value || '');
    setFilters(DEFAULT_FILTERS);
    invalidateResults();
  };

  const fetchAuditEvents = async () => {
    if (!companyId || !moduleKey) {
      setFetchError('Select an account and module before fetching the audit log.');
      return;
    }
    if (filters.startDate && filters.endDate && filters.startDate > filters.endDate) {
      setFetchError('The start date cannot be after the end date.');
      return;
    }

    const query = new URLSearchParams({
      companyNumber: companyId,
      module: moduleKey,
      limit: '500',
    });
    if (selectedModule?.supports_location && filters.locationId) {
      query.set('locationId', filters.locationId);
    }
    if (filters.user.trim()) query.set('user', filters.user.trim());
    if (filters.startDate) query.set('startDate', filters.startDate);
    if (filters.endDate) query.set('endDate', filters.endDate);
    if (filters.search.trim()) query.set('search', filters.search.trim());

    setLoading(true);
    setPage(0);
    setHasFetched(true);
    setFetchError('');
    try {
      const data = await fetchJson(
        `${baseURL}/get_audit_events?${query.toString()}`,
        {},
        'Audit log'
      );
      setEvents(requireArray(data, 'Audit log'));
    } catch (error) {
      setEvents([]);
      setFetchError(error.message || 'The audit log could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setPage(0);
    setModuleKey('');
    setFilters(DEFAULT_FILTERS);
    setEvents([]);
    setHasFetched(false);
    setFetchError('');
  };

  const exportRows = useMemo(() => events.map((event) => ({
    Timestamp: formatTimestamp(event.timestamp),
    Module: event.module_label,
    Action: event.action,
    Record: event.record,
    Location: event.location,
    User: event.user,
    Reason: getAuditChangeReason(event),
    'Changed / recorded data': JSON.stringify(event.changed_data || {}),
  })), [events]);
  const totalPages = Math.max(1, Math.ceil(events.length / PAGE_SIZE));
  const paginatedEvents = useMemo(
    () => events.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [events, page]
  );

  const exportFilename = `audit-${companyId || 'account'}-${moduleKey || 'module'}.csv`;

  return (
    <div className="event-audit">
      <PageHeader
        title="Audit / Event Log"
        subtitle="Trace recorded changes across portal modules from one account-scoped workspace."
        eyebrow="Administration"
        help={(
          <button type="button" className="btn btn-outline-primary" onClick={() => setLgShow(true)}>
            Help
          </button>
        )}
      />

      <Modal
        size="lg"
        show={lgShow}
        onHide={() => setLgShow(false)}
        aria-labelledby="event-log-help-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="event-log-help-title">Using the centralized audit log</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ol className="mb-0">
            <li>Select the account whose history you are permitted to review.</li>
            <li>Select a module and, where available, narrow the results by location.</li>
            <li>Use date, user, and text filters to find a specific recorded change.</li>
            <li>Review a recorded reason directly when available, expand the changed-data cell, or export the rows to CSV.</li>
          </ol>
          <p className="text-muted mt-3 mb-0">
            Audit entries are historical snapshots. Older modules may not contain every normalized field.
          </p>
        </Modal.Body>
      </Modal>

      {setupError && (
        <ErrorState compact title="Audit log is unavailable" description={setupError} />
      )}

      <FormSection
        title="Audit filters"
        description="All queries are restricted to the selected account before any other filter is applied."
      >
        <div className="row g-3">
          <div className="col-12 col-lg-6">
            <label htmlFor="audit_account" className="form-label">Account*</label>
            <Select
              inputId="audit_account"
              name="audit_account"
              value={accountOptions.find((option) => option.value === companyId) || null}
              options={accountOptions}
              placeholder="Select account"
              onChange={(option) => setCompanyId(option?.value || '')}
              isDisabled={accountOptions.length === 1}
              isClearable={accountOptions.length > 1}
            />
          </div>

          <div className="col-12 col-lg-6">
            <label htmlFor="audit_module" className="form-label">Module*</label>
            <Select
              inputId="audit_module"
              name="audit_module"
              value={moduleOptions.find((option) => option.value === moduleKey) || null}
              options={moduleOptions}
              placeholder="Select module"
              onChange={handleModuleChange}
              isClearable
              isDisabled={!companyId || modules.length === 0}
            />
          </div>

          {selectedModule?.supports_location && (
            <div className="col-12 col-lg-6">
              <label htmlFor="audit_location" className="form-label">Location (optional)</label>
              <Select
                inputId="audit_location"
                name="audit_location"
                value={locationOptions.find((option) => option.value === filters.locationId) || null}
                options={locationOptions}
                placeholder="All account locations"
                onChange={(option) => updateFilter('locationId', option?.value || '')}
                isClearable
                noOptionsMessage={() => 'No account locations available'}
              />
            </div>
          )}

          <div className="col-12 col-md-6 col-lg-3">
            <label htmlFor="audit_start_date" className="form-label">From date (optional)</label>
            <input
              id="audit_start_date"
              type="date"
              className="form-control"
              value={filters.startDate}
              max={filters.endDate || undefined}
              onChange={(event) => updateFilter('startDate', event.target.value)}
            />
          </div>

          <div className="col-12 col-md-6 col-lg-3">
            <label htmlFor="audit_end_date" className="form-label">To date (optional)</label>
            <input
              id="audit_end_date"
              type="date"
              className="form-control"
              value={filters.endDate}
              min={filters.startDate || undefined}
              onChange={(event) => updateFilter('endDate', event.target.value)}
            />
          </div>

          <div className="col-12 col-lg-6">
            <label htmlFor="audit_user" className="form-label">Changed by (optional)</label>
            <input
              id="audit_user"
              type="search"
              className="form-control"
              value={filters.user}
              placeholder="Email or user name"
              onChange={(event) => updateFilter('user', event.target.value)}
            />
          </div>

          <div className="col-12 col-lg-6">
            <label htmlFor="audit_search" className="form-label">Search recorded data (optional)</label>
            <input
              id="audit_search"
              type="search"
              className="form-control"
              value={filters.search}
              placeholder="Record, action, value, rate code..."
              onChange={(event) => updateFilter('search', event.target.value)}
            />
          </div>
        </div>

        <div className="event-audit__actions">
          {(moduleKey || hasFetched) && (
            <Button variant="outlined" color="error" onClick={resetFilters} disabled={loading}>
              Reset
            </Button>
          )}
          <Button
            variant="contained"
            onClick={fetchAuditEvents}
            disabled={!companyId || !moduleKey || loading}
          >
            {loading ? 'Fetching...' : 'Fetch audit log'}
          </Button>
        </div>
      </FormSection>

      <DataRegion
        className="event-audit__results"
        title={selectedModule ? `${selectedModule.label} audit history` : 'Audit history'}
        count={hasFetched && !loading ? events.length : undefined}
        countLabel="entries"
        actions={events.length > 0 ? (
          <CSVLink
            data={exportRows}
            filename={exportFilename}
            className="btn btn-outline-success event-audit__export"
          >
            Export visible rows
          </CSVLink>
        ) : null}
      >
        {loading ? (
          <LoadingSkeleton count={7} label="Loading audit history" />
        ) : fetchError ? (
          <ErrorState
            compact
            title="Audit history could not be loaded"
            description={fetchError}
            onRetry={companyId && moduleKey ? fetchAuditEvents : undefined}
          />
        ) : !hasFetched ? (
          <EmptyState
            compact
            title="Choose an account and module"
            description="Optional filters are applied by the server to the selected account's audit records."
          />
        ) : events.length === 0 ? (
          <EmptyState
            compact
            title="No matching audit entries"
            description="No recorded changes matched the selected account, module, and filters."
          />
        ) : (
          <>
            <div className="event-audit__table-wrap">
              <table className="table table-hover align-middle event-audit__table">
              <thead>
                <tr>
                  <th scope="col">Timestamp</th>
                  <th scope="col">Module / action</th>
                  <th scope="col">Record / location</th>
                  <th scope="col">Changed by</th>
                  <th scope="col">Reason</th>
                  <th scope="col">Changed / recorded data</th>
                </tr>
              </thead>
              <tbody>
                {paginatedEvents.map((event, index) => (
                  <tr key={event.id || `${event.timestamp}-${index}`}>
                    <td className="event-audit__timestamp">{formatTimestamp(event.timestamp)}</td>
                    <td>
                      <span className="event-audit__module">{event.module_label}</span>
                      <span className="event-audit__action">{event.action || 'Changed'}</span>
                    </td>
                    <td>
                      <div>{event.record || 'Record not identified'}</div>
                      {event.location && <small className="text-muted">{event.location}</small>}
                    </td>
                    <td>{event.user || 'Not recorded'}</td>
                    <td className="event-audit__reason-cell">
                      {getAuditChangeReason(event) || (
                        <span className="text-muted">Not recorded</span>
                      )}
                    </td>
                    <td className="event-audit__changes-cell">
                      {changedFieldCount(event) > 0 ? (
                        <details>
                          <summary>{changedFieldCount(event)} recorded fields</summary>
                          <pre>{JSON.stringify(event.changed_data, null, 2)}</pre>
                        </details>
                      ) : (
                        <span className="text-muted">No field snapshot recorded</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <nav className="event-audit__pagination" aria-label="Audit history pages">
                <Button
                  size="small"
                  variant="outlined"
                  disabled={page === 0}
                  onClick={() => setPage((current) => Math.max(0, current - 1))}
                >
                  Previous
                </Button>
                <span>Page {page + 1} of {totalPages}</span>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))}
                >
                  Next
                </Button>
              </nav>
            )}
          </>
        )}
      </DataRegion>

      {selectedCompany && (
        <p className="event-audit__scope-note">
          Viewing account scope: <strong>{selectedCompany.company_name} ({selectedCompany._id})</strong>
        </p>
      )}
    </div>
  );
}
